import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import type { ReferenceType } from '../utils/chatUtils';
import parseByDelimiter from '@utils/parseByDelimiter';

interface DataReferenceViewProps {
  type: ReferenceType;
  title: string;
  id: string;
  theme?: boolean;
  onPress?: () => void;
}

const DataReferenceView: React.FC<DataReferenceViewProps> = React.memo(({
  type,
  title,
  id,
  theme = true,
  onPress,
}) => {
  const getIcon = (refType: ReferenceType) => {
    switch (refType) {
      case 'data':
        return 'database';
      case 'item':
        return 'file-document-outline';
      case 'project':
        return 'folder-outline';
      case 'calendar':
        return 'calendar-outline';
      default:
        return 'file-outline';
    }
  };

  const getColor = (refType: ReferenceType) => {
    switch (refType) {
      case 'data':
        return '#3b82f6'; // blue
      case 'item':
        return '#10b981'; // green
      case 'project':
        return '#8b5cf6'; // purple
      case 'calendar':
        return '#f59e0b'; // amber
      default:
        return '#6b7280'; // gray
    }
  };

  const iconColor = getColor(type);
  const backgroundColor = theme ? '#f0f4ff' : '#1e293b';
  const borderColor = theme ? '#cbd5e1' : '#475569';
  const textColor = theme ? '#1e2937' : '#f1f5f9';

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.container,
        {
          backgroundColor,
          borderColor,
          opacity: pressed ? 0.7 : 1,
          shadowColor: iconColor,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.15,
          shadowRadius: 4,
          elevation: Platform.OS === 'android' ? 3 : undefined,
        },
      ]}
    >
      <MaterialCommunityIcons
        name={getIcon(type)}
        size={14}
        color={iconColor}
        style={styles.icon}
      />
      <Text
        style={[styles.title, { color: textColor }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {parseByDelimiter(title) || title}
      </Text>
    </Pressable>
  );
});

DataReferenceView.displayName = 'DataReferenceView';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
    borderRadius: 6,
    borderWidth: 1,
    marginHorizontal: 2,
    marginVertical: 1,
    minHeight: 24,
    maxWidth: '100%',
  },
  icon: {
    marginRight: 6,
  },
  title: {
    fontSize: 13,
    fontWeight: '500',
    flexShrink: 1,
  },
});

export default DataReferenceView;

